/**
 * Dashboards page (LDS) — metrics counters and temporal chart.
 *
 * Displays remediation metrics (success/failure, dry_run/enforce),
 * a temporal line chart (recharts), and filters by period/guardrail.
 * Shows explicit empty state when no data in scope.
 *
 * Requirements: 5.2, 5.4, 5.5, 5.6
 */

import { useState, useEffect, useCallback } from 'react';
import {
  LdsBox,
  LdsPaper,
  LdsTypography,
  LdsFormField,
  LdsTextField,
  LdsSelect,
  LdsOption,
  LdsButton,
  LdsAlert,
  LdsProgressCircular,
} from '@lds/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AxiosInstance } from 'axios';
import styles from './DashboardsPage.module.scss';

export interface MetricsResult {
  totals: {
    success: number;
    failure: number;
    dryRun: number;
    enforce: number;
  };
  timeseries: Array<{ bucket: string; success: number; failure: number }>;
  empty: boolean;
}

export interface DashboardsPageProps {
  apiClient: AxiosInstance;
  guardrails?: string[];
}

const COUNTERS: Array<{ key: keyof MetricsResult['totals']; label: string; testid: string }> = [
  { key: 'success', label: 'Sucesso', testid: 'counter-success' },
  { key: 'failure', label: 'Falha', testid: 'counter-failure' },
  { key: 'dryRun', label: 'Dry Run', testid: 'counter-dryrun' },
  { key: 'enforce', label: 'Enforce', testid: 'counter-enforce' },
];

export function DashboardsPage({ apiClient, guardrails = [] }: DashboardsPageProps) {
  const [metrics, setMetrics] = useState<MetricsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [guardrail, setGuardrail] = useState('');

  const fetchMetrics = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { from, to };
      if (guardrail) params.guardrail = guardrail;
      const response = await apiClient.get<MetricsResult>('/dashboards/metrics', { params });
      setMetrics(response.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load metrics';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, from, to, guardrail]);

  useEffect(() => {
    if (from && to) {
      fetchMetrics();
    }
  }, [fetchMetrics, from, to]);

  return (
    <LdsBox data-testid="dashboards-page">
      <LdsTypography variant="headingXl">Dashboards</LdsTypography>

      {/* Filters */}
      <LdsPaper elevated className={styles.filters}>
        <LdsBox className={styles.filtersRow}>
          <LdsBox className={styles.field}>
            <LdsFormField label="De" fieldId="filter-from">
              <LdsTextField
                type="date"
                value={from}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
                aria-label="Period start date"
                data-field="filter-from"
              />
            </LdsFormField>
          </LdsBox>
          <LdsBox className={styles.field}>
            <LdsFormField label="Até" fieldId="filter-to">
              <LdsTextField
                type="date"
                value={to}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
                aria-label="Period end date"
                data-field="filter-to"
              />
            </LdsFormField>
          </LdsBox>
          {guardrails.length > 0 && (
            <LdsBox className={styles.field}>
              <LdsFormField label="Guardrail" fieldId="filter-guardrail">
                <LdsSelect
                  value={guardrail}
                  onChange={(_e: unknown, value: unknown) => setGuardrail(String(value ?? ''))}
                  aria-label="Filter by guardrail"
                  data-field="filter-guardrail"
                >
                  <LdsOption value="">Todas</LdsOption>
                  {guardrails.map((g) => (
                    <LdsOption key={g} value={g}>
                      {g}
                    </LdsOption>
                  ))}
                </LdsSelect>
              </LdsFormField>
            </LdsBox>
          )}
          <LdsBox className={styles.field}>
            <LdsButton
              variant="contained"
              onClick={fetchMetrics}
              disabled={!from || !to}
              aria-label="Search metrics"
              data-action="filter-search"
            >
              Buscar
            </LdsButton>
          </LdsBox>
        </LdsBox>
      </LdsPaper>

      {loading && (
        <LdsBox className={styles.centered} data-testid="dashboards-loading">
          <LdsProgressCircular indeterminate animated ariaLabel="Carregando" />
        </LdsBox>
      )}

      {error && <LdsAlert color="critical" description={error} data-testid="dashboards-error" />}

      {metrics?.empty && (
        <LdsAlert
          color="info"
          description="Nenhum registro de remediação para o período e filtros selecionados."
          data-testid="dashboards-empty"
        />
      )}

      {metrics && !metrics.empty && (
        <>
          <LdsBox className={styles.counters}>
            {COUNTERS.map(({ key, label, testid }) => (
              <LdsPaper key={key} elevated>
                <LdsBox className={styles.counterCard}>
                  <LdsTypography variant="bodySm">{label}</LdsTypography>
                  <LdsTypography variant="displaySm">
                    <span data-testid={testid}>{metrics.totals[key]}</span>
                  </LdsTypography>
                </LdsBox>
              </LdsPaper>
            ))}
          </LdsBox>

          <LdsPaper elevated>
            <LdsBox className={styles.chartCard}>
              <LdsTypography variant="headingMd">Remediações ao longo do tempo</LdsTypography>
              <LdsBox className={styles.chart} data-testid="temporal-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics.timeseries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="success" stroke="#008557" name="Sucesso" />
                    <Line type="monotone" dataKey="failure" stroke="#d32f2f" name="Falha" />
                  </LineChart>
                </ResponsiveContainer>
              </LdsBox>
            </LdsBox>
          </LdsPaper>
        </>
      )}
    </LdsBox>
  );
}
